/** Pure LOD-selector state for the model viewer — no DOM, fully unit-testable. */

/** The per-LOD fields the selector reads; a stale `data/baked` has only the first two. */
export interface LodLike {
  readonly index: number;
  readonly triangleCount: number;
  readonly detail?: number;
  readonly lines?: { readonly indices: readonly number[] };
  readonly billboards?: readonly { readonly stateIndex: number }[];
  readonly spheres?: readonly { readonly stateIndex: number }[];
}

export interface LodOption {
  readonly index: number;
  readonly triangleCount: number;
  /** What the dropdown shows — every primitive kind the LOD actually has, not just triangles. */
  readonly label: string;
}

/** Clamps a requested LOD index into `[0, lodCount - 1]`. Returns 0 for an empty/invalid `lodCount`. */
export function clampLodIndex(requested: number, lodCount: number): number {
  if (lodCount <= 0) return 0;
  return Math.min(Math.max(requested, 0), lodCount - 1);
}

/**
 * The default LOD to show a freshly-selected model at: the most detailed
 * one available (spec docs/formats/models-tbl.md's "Notes for the port" —
 * LOD 0/1 are line-and-plate impostors, the highest-numbered LOD is the
 * useful one), i.e. the last entry, not necessarily index 3 — some models
 * have fewer than 4 LODs.
 */
export function defaultLodIndex(lodCount: number): number {
  return clampLodIndex(lodCount - 1, lodCount);
}

/**
 * How the dropdown names one LOD. A bare triangle count reads "0 tri" for
 * all 55 sprite/line-only objects in the corpus (`FLAK`, `EXPLO`, `SMOKE`,
 * every `TERR*` road and river), which is exactly what made them look
 * broken, so each primitive kind is listed when the LOD has any.
 */
export function lodLabel(lod: LodLike): string {
  const parts = [`${lod.triangleCount} tri`];
  const lineCount = (lod.lines?.indices.length ?? 0) / 2;
  if (lineCount > 0) parts.push(`${lineCount} lines`);
  if (lod.billboards && lod.billboards.length > 0) parts.push(`${lod.billboards.length} sprites`);
  if (lod.spheres && lod.spheres.length > 0) parts.push(`${lod.spheres.length} spheres`);
  return `LOD ${lod.index} (${parts.join(", ")})`;
}

/** Builds the dropdown's option list in ascending index order. */
export function lodOptions(lods: readonly LodLike[]): LodOption[] {
  return [...lods]
    .sort((a, b) => a.index - b.index)
    .map((lod) => ({ index: lod.index, triangleCount: lod.triangleCount, label: lodLabel(lod) }));
}

/**
 * How many alternate renderings the object has, i.e. one past the largest
 * `stateIndex` on any sprite or sphere (docs/formats/models-tbl.md §4.3/§4.5
 * — the engine draws node `stateArray[group.stateChannel]` of each group).
 * 1 means "nothing to choose", which is every object without sprite nodes.
 */
export function stateCount(lods: readonly LodLike[]): number {
  let highest = 0;
  for (const lod of lods) {
    for (const item of [...(lod.billboards ?? []), ...(lod.spheres ?? [])]) {
      highest = Math.max(highest, item.stateIndex + 1);
    }
  }
  return Math.max(highest, 1);
}

/**
 * The highest `detail` any LOD declares — §3's apparent-size switching
 * threshold. Paired with `boundingRadius` it is the only world-space scale
 * the file offers for a screen-space sprite (see `model-sprites.ts`).
 * Returns 0 when the baked data predates the field, which makes
 * `metresPerSpritePixel` fall back rather than divide by nothing.
 */
export function maxLodDetail(lods: readonly LodLike[]): number {
  return lods.reduce((max, lod) => Math.max(max, lod.detail ?? 0), 0);
}
